import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Store, Package, Eye, Layers, Truck, Undo2, IndianRupee, type LucideIcon } from 'lucide-react';
import axios from 'axios';
import { AsinIdentity, buildAsinCatalog, type AsinCatalog } from '@/components/retail/AsinIdentity';
import { RetailDataTable as DataTable } from '@/components/retail/RetailDataTable';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  RETAIL_IDENTITY_FILL_CELL_CLASS,
  RETAIL_TRAILING_METRIC_CELL_CLASS,
} from '@/components/retail/retailTableLayout';
import {
  clipRangeToVendorLag2,
  latestInventoryRows,
  rollupSalesByAsin,
  rollupTrafficByAsin,
  sumGlanceViews,
  summarizeVendorSales,
  type VendorSalesAsinRollup,
  type VendorSalesTotals,
  type VendorTrafficAsinRollup,
} from '@/utils/vendorRetailMetrics';
import { isRangeValid } from '@/api/reportDateRanges';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import {
  spApiOauthApi,
  type SpApiAsinRow,
  type SpApiBrandAnalyticsCatalogRow,
  type SpApiBrandAnalyticsSearchTermRow,
  type SpApiInventoryRow,
  type SpApiMarketBasketRow,
  type SpApiOauthConnection,
  type SpApiOrderRow,
  type SpApiRepeatPurchaseRow,
  type SpApiReviewRow,
} from '@/api/client';

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      if ('message' in detail && typeof (detail as { message: unknown }).message === 'string') {
        return (detail as { message: string }).message;
      }
      if ('error' in detail) return String((detail as { error: unknown }).error);
      try {
        return JSON.stringify(detail);
      } catch {
        /* ignore */
      }
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

function formatNum(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
}

function formatMoney(amount: number | null | undefined, currency?: string | null): string {
  return formatCurrency(amount, { currencyCode: currency ?? null }, {
    empty: '—',
    maximumFractionDigits: 2,
  });
}

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${(Number(value) * 100).toFixed(2)}%`;
}

function emptyMessage(syncing: boolean | undefined, fallback: string): string {
  return syncing
    ? 'Retail reports are syncing. Rows will appear here once the import finishes.'
    : fallback;
}

function retailAccountLabel(connection: SpApiOauthConnection): string {
  const kind = connection.connection_type === 'seller' ? 'Seller' : 'Vendor';
  return connection.display_name || `${kind} · ${connection.selling_partner_id}`;
}

function LoadingBlock() {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      {message}
      <p className="mt-2 text-red-700/80">
        Retail reports can take a few minutes. Retry, or remap the Seller or Vendor Central account
        on Connections if authorization expired.
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function formatLag2Label(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function VendorKpiStrip({
  totals,
  glanceViews,
  loading,
  trafficLabel = 'Glance views',
}: {
  totals: VendorSalesTotals;
  glanceViews: number;
  loading: boolean;
  trafficLabel?: string;
}) {
  const cards: { key: string; label: string; value: string; icon: LucideIcon; iconClassName: string }[] = [
    {
      key: 'revenue',
      label: 'Ordered revenue',
      value: formatMoney(totals.orderedRevenue, totals.currency || 'INR'),
      icon: IndianRupee,
      iconClassName: 'bg-sky-50 text-sky-600',
    },
    {
      key: 'units',
      label: 'Ordered units',
      value: formatNum(totals.orderedUnits),
      icon: Package,
      iconClassName: 'bg-emerald-50 text-emerald-600',
    },
    {
      key: 'gv',
      label: trafficLabel,
      value: formatNum(glanceViews),
      icon: Eye,
      iconClassName: 'bg-indigo-50 text-indigo-600',
    },
    {
      key: 'asins',
      label: 'ASINs with sales',
      value: formatNum(totals.asinCount),
      icon: Layers,
      iconClassName: 'bg-violet-50 text-violet-600',
    },
    {
      key: 'shipped',
      label: 'Shipped units',
      value: formatNum(totals.shippedUnits),
      icon: Truck,
      iconClassName: 'bg-blue-50 text-blue-600',
    },
    {
      key: 'returns',
      label: 'Returns',
      value: formatNum(totals.customerReturns),
      icon: Undo2,
      iconClassName: 'bg-rose-50 text-rose-500',
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.key}
            className="flex min-h-[5.5rem] items-center gap-3 p-3 shadow-sm"
          >
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                card.iconClassName
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground-muted">{card.label}</p>
              {loading ? (
                <div className="mt-1 h-6 w-20 animate-pulse rounded bg-surface-muted" />
              ) : (
                <p className="truncate font-mono text-lg font-bold tabular-nums leading-tight text-foreground">
                  {card.value}
                </p>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function SalesTable({
  rows,
  syncing,
  catalog,
}: {
  rows: VendorSalesAsinRollup[];
  syncing?: boolean;
  catalog: AsinCatalog;
}) {
  return (
    <DataTable
      empty={emptyMessage(syncing, 'No sales rows for this mapped account in the selected range.')}
      columns={[
        { key: 'asin', label: 'ASIN', identity: true },
        { key: 'orderedUnits', label: 'Ordered units', align: 'right' },
        { key: 'orderedRev', label: 'Ordered revenue', align: 'right' },
        { key: 'shippedUnits', label: 'Shipped units', align: 'right' },
        { key: 'shippedRev', label: 'Shipped revenue', align: 'right' },
        { key: 'returns', label: 'Returns', align: 'right' },
      ]}
      rows={rows.map((r) => ({
        asin: <AsinIdentity asin={r.asin} catalog={catalog} />,
        orderedUnits: formatNum(r.orderedUnits),
        orderedRev: formatMoney(r.orderedRevenue, r.currency),
        shippedUnits: formatNum(r.shippedUnits),
        shippedRev: formatMoney(r.shippedRevenue, r.currency),
        returns: formatNum(r.customerReturns),
      }))}
    />
  );
}

function AsinsTable({ rows, syncing }: { rows: SpApiAsinRow[]; syncing?: boolean }) {
  return (
    <DataTable
      empty={emptyMessage(syncing, 'No ASINs returned for this mapped account.')}
      columns={[
        { key: 'product', label: 'ASIN', identity: true },
        { key: 'endDate', label: 'As of' },
        { key: 'sellable', label: 'Sellable OH', align: 'right' },
        { key: 'openPo', label: 'Open PO', align: 'right' },
      ]}
      rows={rows.map((r) => ({
        product: <AsinIdentity asin={r.asin} title={r.title} imageUrl={r.imageUrl} />,
        endDate: r.endDate || '—',
        sellable: formatNum(r.sellableOnHandInventoryUnits),
        openPo: formatNum(r.openPurchaseOrderUnits),
      }))}
    />
  );
}

function InventoryTable({
  rows,
  asOf,
  syncing,
  catalog,
}: {
  rows: SpApiInventoryRow[];
  asOf: string | null;
  syncing?: boolean;
  catalog: AsinCatalog;
}) {
  return (
    <div className="space-y-3">
      {asOf ? (
        <p className="text-sm text-foreground-muted">
          Snapshot as of <span className="font-semibold text-foreground">{asOf}</span>
        </p>
      ) : null}
      <DataTable
        empty={emptyMessage(syncing, 'No inventory rows for this mapped vendor in the selected range.')}
        columns={[
          { key: 'asin', label: 'ASIN', identity: true },
          { key: 'sellable', label: 'Sellable OH', align: 'right' },
          { key: 'unsellable', label: 'Unsellable', align: 'right' },
          { key: 'aged', label: 'Aged 90d+', align: 'right' },
          { key: 'unhealthy', label: 'Unhealthy', align: 'right' },
          { key: 'openPo', label: 'Open PO', align: 'right' },
        ]}
        rows={rows.map((r) => ({
          asin: <AsinIdentity asin={r.asin} catalog={catalog} />,
          sellable: formatNum(r.sellableOnHandInventoryUnits),
          unsellable: formatNum(r.unsellableOnHandInventoryUnits),
          aged: formatNum(r.aged90PlusDaysSellableInventoryUnits),
          unhealthy: formatNum(r.unhealthyInventoryUnits),
          openPo: formatNum(r.openPurchaseOrderUnits),
        }))}
      />
    </div>
  );
}

function OrdersTable({ rows, catalog }: { rows: SpApiOrderRow[]; catalog: AsinCatalog }) {
  return (
    <DataTable
      empty="No purchase orders in the selected window (Vendor Orders API uses a max 7-day range)."
      columns={[
        { key: 'po', label: 'PO #' },
        { key: 'state', label: 'State' },
        { key: 'date', label: 'Date' },
        { key: 'asin', label: 'ASIN', identity: true },
        { key: 'vendorSku', label: 'Vendor SKU' },
        { key: 'ordered', label: 'Ordered', align: 'right' },
        { key: 'accepted', label: 'Accepted', align: 'right' },
        { key: 'received', label: 'Received', align: 'right' },
      ]}
      rows={rows.map((r) => ({
        po: r.purchaseOrderNumber || '—',
        state: r.purchaseOrderState || '—',
        date: r.purchaseOrderDate || '—',
        asin: <AsinIdentity asin={r.asin} title={r.title} catalog={catalog} />,
        vendorSku: r.vendorProductIdentifier || '—',
        ordered: formatNum(r.orderedQuantity),
        accepted: formatNum(r.acceptedQuantity),
        received: formatNum(r.receivedQuantity),
      }))}
    />
  );
}

function TrafficTable({
  rows,
  syncing,
  catalog,
  metricLabel = 'Glance views',
}: {
  rows: VendorTrafficAsinRollup[];
  syncing?: boolean;
  catalog: AsinCatalog;
  metricLabel?: string;
}) {
  return (
    <DataTable
      empty={emptyMessage(
        syncing,
        metricLabel === 'Page views'
          ? 'No page-view rows for this mapped account in the selected range.'
          : 'No traffic / glance-view rows for this mapped account in the selected range.'
      )}
      columns={[
        {
          key: 'asin',
          label: 'ASIN',
          identity: true,
          className: RETAIL_IDENTITY_FILL_CELL_CLASS,
        },
        {
          key: 'gv',
          label: metricLabel,
          align: 'right',
          className: RETAIL_TRAILING_METRIC_CELL_CLASS,
        },
      ]}
      rows={rows.map((r) => ({
        asin: <AsinIdentity asin={r.asin} catalog={catalog} />,
        gv: formatNum(r.glanceViews),
      }))}
    />
  );
}

function RepeatPurchaseTable({
  rows,
  syncing,
  catalog,
}: {
  rows: SpApiRepeatPurchaseRow[];
  syncing?: boolean;
  catalog: AsinCatalog;
}) {
  return (
    <DataTable
      empty={emptyMessage(syncing, 'No repeat-purchase rows (connection may lack Brand Analytics).')}
      columns={[
        { key: 'asin', label: 'ASIN', identity: true },
        { key: 'orders', label: 'Orders', align: 'right' },
        { key: 'customers', label: 'Unique customers', align: 'right' },
        { key: 'repeatPct', label: 'Repeat customers', align: 'right' },
        { key: 'revenue', label: 'Repeat revenue', align: 'right' },
        { key: 'revenuePct', label: 'Repeat revenue %', align: 'right' },
        { key: 'start', label: 'Start' },
        { key: 'end', label: 'End' },
      ]}
      rows={rows.map((r) => ({
        asin: <AsinIdentity asin={r.asin} catalog={catalog} />,
        orders: formatNum(r.orders),
        customers: formatNum(r.uniqueCustomers),
        repeatPct: formatPct(r.repeatCustomersPctTotal),
        revenue: formatMoney(r.repeatPurchaseRevenue, r.currency),
        revenuePct: formatPct(r.repeatPurchaseRevenuePctTotal),
        start: r.startDate || '—',
        end: r.endDate || '—',
      }))}
    />
  );
}

function MarketBasketTable({
  rows,
  syncing,
  catalog,
}: {
  rows: SpApiMarketBasketRow[];
  syncing?: boolean;
  catalog: AsinCatalog;
}) {
  return (
    <DataTable
      empty={emptyMessage(syncing, 'No market-basket rows (connection may lack Brand Analytics).')}
      columns={[
        { key: 'asin', label: 'ASIN', identity: true },
        { key: 'withAsin', label: 'Purchased with', identity: true },
        { key: 'rank', label: 'Rank', align: 'right' },
        { key: 'combo', label: 'Combination %', align: 'right' },
        { key: 'start', label: 'Start' },
        { key: 'end', label: 'End' },
      ]}
      rows={rows.map((r) => ({
        asin: <AsinIdentity asin={r.asin} catalog={catalog} />,
        withAsin: <AsinIdentity asin={r.purchasedWithAsin} catalog={catalog} />,
        rank: formatNum(r.purchasedWithRank),
        combo: formatPct(r.combinationPct),
        start: r.startDate || '—',
        end: r.endDate || '—',
      }))}
    />
  );
}

function BrandAnalyticsTables({
  catalog,
  searchTerms,
  repeatPurchase,
  marketBasket,
  syncing,
  asinCatalog,
}: {
  catalog: SpApiBrandAnalyticsCatalogRow[];
  searchTerms: SpApiBrandAnalyticsSearchTermRow[];
  repeatPurchase: SpApiRepeatPurchaseRow[];
  marketBasket: SpApiMarketBasketRow[];
  syncing?: boolean;
  asinCatalog: AsinCatalog;
}) {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Search catalog performance</h3>
        <DataTable
          empty={emptyMessage(
            syncing,
            'No Brand Analytics catalog performance rows (connection may lack Brand Analytics).'
          )}
          columns={[
            { key: 'asin', label: 'ASIN', identity: true },
            { key: 'impressions', label: 'Impressions', align: 'right' },
            { key: 'clicks', label: 'Clicks', align: 'right' },
            { key: 'cartAdds', label: 'Cart adds', align: 'right' },
            { key: 'purchases', label: 'Purchases', align: 'right' },
            { key: 'sales', label: 'Search sales', align: 'right' },
            { key: 'cvr', label: 'CVR', align: 'right' },
          ]}
          rows={catalog.map((r) => ({
            asin: <AsinIdentity asin={r.asin} catalog={asinCatalog} />,
            impressions: formatNum(r.impressionCount),
            clicks: formatNum(r.clickCount),
            cartAdds: formatNum(r.cartAddCount),
            purchases: formatNum(r.purchaseCount),
            sales: formatMoney(r.searchTrafficSales, r.currency),
            cvr: formatPct(r.conversionRate),
          }))}
        />
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Search terms</h3>
        <DataTable
          empty={emptyMessage(syncing, 'No search-terms rows for this mapped vendor.')}
          columns={[
            { key: 'dept', label: 'Department' },
            { key: 'term', label: 'Search term' },
            { key: 'rank', label: 'Freq rank', align: 'right' },
            { key: 'asin', label: 'Clicked ASIN', identity: true },
            { key: 'clickShare', label: 'Click share', align: 'right' },
            { key: 'convShare', label: 'Conv share', align: 'right' },
          ]}
          rows={searchTerms.map((r) => ({
            dept: r.departmentName || '—',
            term: r.searchTerm || '—',
            rank: formatNum(r.searchFrequencyRank),
            asin: <AsinIdentity asin={r.clickedAsin} catalog={asinCatalog} />,
            clickShare: formatPct(r.clickShare),
            convShare: formatPct(r.conversionShare),
          }))}
        />
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Repeat purchase</h3>
        <RepeatPurchaseTable rows={repeatPurchase} syncing={syncing} catalog={asinCatalog} />
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Market basket</h3>
        <MarketBasketTable rows={marketBasket} syncing={syncing} catalog={asinCatalog} />
      </div>
    </div>
  );
}

function ReviewsTable({
  rows,
  note,
  syncing,
  catalog,
}: {
  rows: SpApiReviewRow[];
  note?: string;
  syncing?: boolean;
  catalog: AsinCatalog;
}) {
  return (
    <div className="space-y-3">
      {note ? <p className="text-sm text-foreground-muted">{note}</p> : null}
      <DataTable
        empty={emptyMessage(syncing, 'No review topics returned for the sampled ASINs.')}
        columns={[
          { key: 'asin', label: 'ASIN', identity: true },
          { key: 'sentiment', label: 'Sentiment' },
          { key: 'topic', label: 'Topic' },
          { key: 'mentions', label: 'Mentions', align: 'right' },
          { key: 'impact', label: 'Star rating impact', align: 'right' },
        ]}
        rows={rows.map((r) => ({
          asin: <AsinIdentity asin={r.asin} title={r.itemName} catalog={catalog} />,
          sentiment: r.sentiment || (r.error ? `error: ${r.error}` : '—'),
          topic: r.topic || '—',
          mentions: formatNum(r.mentionCount),
          impact: r.starRatingImpact != null ? String(r.starRatingImpact) : '—',
        }))}
      />
    </div>
  );
}

export function RetailDataPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { range: rangeState } = useAppDateRange();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCredentialId, setSelectedCredentialId] = useState('');

  const queryRange = useMemo(
    () =>
      clipRangeToVendorLag2(rangeState.current.startDate, rangeState.current.endDate),
    [rangeState.current.startDate, rangeState.current.endDate]
  );
  const rangeOk =
    isRangeValid(rangeState.current) && queryRange.startDate <= queryRange.endDate;

  const resolveQuery = useQuery({
    queryKey: ['sp-api-resolve', profileId],
    queryFn: async () => {
      const res = await spApiOauthApi.resolveConnection({ profileId });
      if (!res.success || !res.data) throw new Error('Failed to resolve retail connection');
      return res.data;
    },
    enabled: Boolean(profileId),
    staleTime: 60_000,
  });

  const mappedConnections = useMemo(() => {
    const listed = resolveQuery.data?.connections ?? [];
    if (listed.length > 0) return listed;
    return resolveQuery.data?.connection ? [resolveQuery.data.connection] : [];
  }, [resolveQuery.data]);

  useEffect(() => {
    const ids = mappedConnections.map((c) => c.id);
    if (ids.length === 0) {
      setSelectedCredentialId('');
      return;
    }
    setSelectedCredentialId((current) => (current && ids.includes(current) ? current : ids[0]));
  }, [mappedConnections]);

  const connection =
    mappedConnections.find((c) => c.id === selectedCredentialId) ?? mappedConnections[0] ?? null;
  const credentialId = connection?.id ?? '';
  const isSeller = connection?.connection_type === 'seller';
  const rangeParams = { start: queryRange.startDate, end: queryRange.endDate };
  const queriesEnabled = Boolean(credentialId) && rangeOk;
  const vendorOnlyQueriesEnabled = queriesEnabled && !isSeller;

  const salesQuery = useQuery({
    queryKey: ['sp-api-retail-sales', credentialId, queryRange.startDate, queryRange.endDate, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getSales(credentialId, rangeParams);
      if (!res.success || !res.data) throw new Error('Failed to load sales');
      return res.data;
    },
    enabled: queriesEnabled,
    staleTime: 60_000,
  });

  const asinsQuery = useQuery({
    queryKey: ['sp-api-retail-asins', credentialId, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getAsins(credentialId, {
        refresh: refreshKey > 0,
      });
      if (!res.success || !res.data) throw new Error('Failed to load ASINs');
      return res.data;
    },
    enabled: Boolean(credentialId),
    staleTime: 60_000,
  });

  const asinCatalog = useMemo(
    () => buildAsinCatalog(asinsQuery.data?.asins ?? []),
    [asinsQuery.data?.asins]
  );

  const inventoryQuery = useQuery({
    queryKey: [
      'sp-api-retail-inventory',
      credentialId,
      queryRange.startDate,
      queryRange.endDate,
      refreshKey,
    ],
    queryFn: async () => {
      const res = await spApiOauthApi.getInventory(credentialId, rangeParams);
      if (!res.success || !res.data) throw new Error('Failed to load inventory');
      return res.data;
    },
    enabled: vendorOnlyQueriesEnabled,
    staleTime: 60_000,
  });

  const ordersQuery = useQuery({
    queryKey: ['sp-api-retail-orders', credentialId, queryRange.startDate, queryRange.endDate, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getOrders(credentialId, rangeParams);
      if (!res.success || !res.data) throw new Error('Failed to load orders');
      return res.data;
    },
    enabled: vendorOnlyQueriesEnabled,
    staleTime: 60_000,
  });

  const trafficQuery = useQuery({
    queryKey: [
      'sp-api-retail-traffic',
      credentialId,
      queryRange.startDate,
      queryRange.endDate,
      refreshKey,
    ],
    queryFn: async () => {
      const res = await spApiOauthApi.getTraffic(credentialId, rangeParams);
      if (!res.success || !res.data) throw new Error('Failed to load traffic');
      return res.data;
    },
    enabled: queriesEnabled,
    staleTime: 60_000,
  });

  const brandQuery = useQuery({
    queryKey: ['sp-api-retail-brand', credentialId, queryRange.startDate, queryRange.endDate, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getBrandAnalytics(credentialId, rangeParams);
      if (!res.success || !res.data) throw new Error('Failed to load brand analytics');
      return res.data;
    },
    enabled: vendorOnlyQueriesEnabled,
    staleTime: 60_000,
  });

  const reviewsQuery = useQuery({
    queryKey: ['sp-api-retail-reviews', credentialId, refreshKey, asinsQuery.dataUpdatedAt],
    queryFn: async () => {
      const asinSample = (asinsQuery.data?.asins ?? [])
        .slice(0, 20)
        .map((r) => r.asin)
        .filter(Boolean)
        .join(',');
      const res = await spApiOauthApi.getReviews(credentialId, {
        ...(asinSample ? { asins: asinSample } : {}),
        max_asins: 20,
      });
      if (!res.success || !res.data) throw new Error('Failed to load reviews');
      return res.data;
    },
    enabled: Boolean(credentialId) && !isSeller && !asinsQuery.isLoading,
    staleTime: 60_000,
  });

  const salesRows = salesQuery.data?.sales_by_asin ?? [];
  const salesTotals = useMemo(() => summarizeVendorSales(salesRows), [salesRows]);
  const salesByAsin = useMemo(() => rollupSalesByAsin(salesRows), [salesRows]);
  const trafficRows = trafficQuery.data?.rows ?? [];
  const trafficByAsin = useMemo(() => rollupTrafficByAsin(trafficRows), [trafficRows]);
  const glanceViews = useMemo(() => sumGlanceViews(trafficRows), [trafficRows]);
  const inventoryLatest = useMemo(
    () => latestInventoryRows(inventoryQuery.data?.rows ?? []),
    [inventoryQuery.data?.rows]
  );

  const brandCatalog = brandQuery.data?.catalog_performance?.rows ?? [];
  const brandSearchTerms = brandQuery.data?.search_terms?.rows ?? [];
  const brandRepeat = brandQuery.data?.repeat_purchase?.rows ?? [];
  const brandBasket = brandQuery.data?.market_basket ?? [];
  const hasBrandRows =
    brandCatalog.length > 0 ||
    brandSearchTerms.length > 0 ||
    brandRepeat.length > 0 ||
    brandBasket.length > 0;
  const orderRows = ordersQuery.data?.rows ?? [];
  const reviewRows = reviewsQuery.data?.rows ?? [];

  const activeFetching = useMemo(
    () =>
      salesQuery.isFetching ||
      asinsQuery.isFetching ||
      inventoryQuery.isFetching ||
      ordersQuery.isFetching ||
      reviewsQuery.isFetching ||
      brandQuery.isFetching ||
      trafficQuery.isFetching,
    [
      salesQuery.isFetching,
      asinsQuery.isFetching,
      inventoryQuery.isFetching,
      ordersQuery.isFetching,
      reviewsQuery.isFetching,
      brandQuery.isFetching,
      trafficQuery.isFetching,
    ]
  );

  const title = connection ? retailAccountLabel(connection) : 'Retail';
  const trafficLabel = isSeller ? 'Page views' : 'Glance views';
  const kpisLoading = salesQuery.isLoading || trafficQuery.isLoading;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-surface">
            <Store className="h-4 w-4 text-foreground-muted" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-headline-md text-foreground">{title}</h1>
            {connection ? (
              <p className="mt-1 text-body-sm text-foreground-muted">
                {connection.region}
                {connection.marketplace_id ? ` · ${connection.marketplace_id}` : ''}
                {resolveQuery.data?.matched_marketplace === false
                  ? ' · marketplace fallback'
                  : ''}
              </p>
            ) : null}
            {queryRange.clipped ? (
              <p className="mt-1 text-sm text-foreground-muted">
                {isSeller ? 'Seller' : connection ? 'Vendor' : 'Retail'} reports through{' '}
                {formatLag2Label(queryRange.dataThrough)} (lag 2 days).
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          {mappedConnections.length > 1 ? (
            <select
              aria-label="Mapped retail account"
              value={credentialId}
              onChange={(event) => setSelectedCredentialId(event.target.value)}
              className="h-[34px] min-w-[12rem] max-w-full rounded-lg border border-border bg-surface px-2 text-sm"
            >
              {mappedConnections.map((item) => (
                <option key={item.id} value={item.id}>
                  {retailAccountLabel(item)}
                </option>
              ))}
            </select>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            className="h-[34px]"
            disabled={activeFetching || !credentialId}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {activeFetching ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {resolveQuery.isLoading ? (
        <LoadingBlock />
      ) : resolveQuery.isError ? (
        <ErrorBlock message={extractError(resolveQuery.error)} />
      ) : !connection ? (
        <div className="rounded-lg border border-border bg-surface px-4 py-10 text-center">
          <p className="text-sm text-foreground-muted">
            {resolveQuery.data?.message ||
              'No Seller or Vendor Central account is mapped to this Ads profile.'}
          </p>
          <Link
            to="/connections?tab=ads"
            className="mt-3 inline-block text-sm font-medium text-brand hover:underline"
          >
            Map a Seller or Vendor Central account on Connections
          </Link>
        </div>
      ) : (
        <div className="space-y-10">
          {salesQuery.isError ? (
            <ErrorBlock message={extractError(salesQuery.error)} />
          ) : (
            <VendorKpiStrip
              totals={salesTotals}
              glanceViews={glanceViews}
              loading={kpisLoading}
              trafficLabel={trafficLabel}
            />
          )}

          <Section title="Sales by ASIN">
            {salesQuery.isLoading ? (
              <LoadingBlock />
            ) : salesQuery.isError ? (
              <ErrorBlock message={extractError(salesQuery.error)} />
            ) : (
              <SalesTable
                rows={salesByAsin}
                syncing={salesQuery.data?.syncing}
                catalog={asinCatalog}
              />
            )}
          </Section>

          <Section title="Traffic by ASIN">
            {trafficQuery.isLoading ? (
              <LoadingBlock />
            ) : trafficQuery.isError ? (
              <ErrorBlock message={extractError(trafficQuery.error)} />
            ) : (
              <TrafficTable
                rows={trafficByAsin}
                syncing={trafficQuery.data?.syncing}
                catalog={asinCatalog}
                metricLabel={trafficLabel}
              />
            )}
          </Section>

          {!isSeller ? (
            <Section title="Inventory">
              {inventoryQuery.isLoading ? (
                <LoadingBlock />
              ) : inventoryQuery.isError ? (
                <ErrorBlock message={extractError(inventoryQuery.error)} />
              ) : (
                <InventoryTable
                  rows={inventoryLatest.rows}
                  asOf={inventoryLatest.asOf}
                  syncing={inventoryQuery.data?.syncing}
                  catalog={asinCatalog}
                />
              )}
            </Section>
          ) : null}

          <Section title="ASINs">
            {asinsQuery.isLoading ? (
              <LoadingBlock />
            ) : asinsQuery.isError ? (
              <ErrorBlock message={extractError(asinsQuery.error)} />
            ) : (
              <AsinsTable
                rows={asinsQuery.data?.asins ?? []}
                syncing={asinsQuery.data?.syncing}
              />
            )}
          </Section>

          {!isSeller ? (
            <>
              {reviewsQuery.isLoading || asinsQuery.isLoading ? null : reviewsQuery.isError ? (
                <p className="text-sm text-foreground-muted">
                  Reviews unavailable: {extractError(reviewsQuery.error)}
                </p>
              ) : reviewRows.length > 0 ? (
                <Section title="Reviews">
                  <ReviewsTable
                    rows={reviewRows}
                    note={reviewsQuery.data?.note}
                    syncing={reviewsQuery.data?.syncing}
                    catalog={asinCatalog}
                  />
                </Section>
              ) : (
                <p className="text-sm text-foreground-muted">
                  {reviewsQuery.data?.note ||
                    'No review topics for this vendor (India marketplace is not supported).'}
                </p>
              )}

              {brandQuery.isLoading ? null : brandQuery.isError ? (
                <p className="text-sm text-foreground-muted">
                  Brand analytics unavailable: {extractError(brandQuery.error)}
                </p>
              ) : hasBrandRows ? (
                <Section title="Brand analytics">
                  <BrandAnalyticsTables
                    catalog={brandCatalog}
                    searchTerms={brandSearchTerms}
                    repeatPurchase={brandRepeat}
                    marketBasket={brandBasket}
                    syncing={brandQuery.data?.syncing}
                    asinCatalog={asinCatalog}
                  />
                </Section>
              ) : (
                <p className="text-sm text-foreground-muted">
                  No brand analytics rows for this vendor in the selected range.
                </p>
              )}

              {ordersQuery.isLoading ? null : ordersQuery.isError ? (
                <p className="text-sm text-foreground-muted">
                  Purchase orders unavailable: {extractError(ordersQuery.error)}
                </p>
              ) : orderRows.length > 0 ? (
                <Section title="Orders">
                  <OrdersTable rows={orderRows} catalog={asinCatalog} />
                </Section>
              ) : (
                <p className="text-sm text-foreground-muted">
                  No purchase orders in the selected window.
                </p>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
